<template>
  <v-card class="pb-5 rounded-lg d-flex flex-column" style="height: 100%;">
    <v-card-title class="primary white--text headline rounded-t-lg d-flex justify-space-between align-center full-width" style="height: 64px">
      <span class="mb-2">{{ $t('speciesRemainingByDestination') }}</span>
      <v-select
        v-model="selectedLocation"
        :items="locationOptions"
        :label="$t('destination')"
        item-text="name"
        item-value="locationId"
        return-object
        dense
        outlined
        hide-details
        :loading="locationsLoading"
        dark
        class="ml-4"
        style="max-width: 300px; min-width: 120px;"
      />
    </v-card-title>
    <v-row no-gutters class="flex-grow-1 align-center">
      <v-col cols="12">
        <LoadableComponent :loading="loading" type="dashboard-widget">
          <div style="max-height: 550px; overflow-y: auto;">
            <AllocationChart
              v-if="chartData.length > 0"
              :volumeBasedCruiseData="chartData"
              style="width: 95%"
              class="mx-auto"
            />
            <div v-else class="text-center py-4 grey--text">
              {{ $t('noAllocationsToChart') }}
            </div>
          </div>
        </LoadableComponent>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AllocationSummary',

  components: {
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    AllocationChart: () => import('@/components/tract/cruise-data/AllocationChart.vue')
  },

  data: () => ({
    loading: false,
    chartData: [],
    selectedLocation: null
  }),

  watch: {
    selectedLocation: {
      handler: 'updateChartData'
    },
    allLocations: {
      handler (locations) {
        if (locations.length > 0 && !this.selectedLocation) {
          this.selectedLocation = this.locationOptions[0]
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('locations', ['allLocations', 'allLocationsByAccountId', 'locationsLoading']),

    locationOptions () {
      const allOption = { locationId: -1, name: this.$t('allDestinations'), accountId: null }
      const internalLocations = this.allLocations.filter(l => l.locationId !== -1 && l.accountId)
      return [allOption, ...internalLocations]
    }
  },

  methods: {
    ...mapActions('tract-contents', ['fetchTractContentAllocationSummaryByDestination', 'fetchTractContentAllocationSummary']),

    async fetchSingleDestination (location) {
      const summary = await this.fetchTractContentAllocationSummaryByDestination(location.accountId)
      this.chartData = summary.map(item => ({
        species: item.species,
        tractContentAllocations: [{
          destinationAccountName: location.name,
          expected: item.sumExpected,
          harvested: item.sumHarvested
        }],
        harvestUpdatedOn: item.harvestUpdatedOn
      }))
    },

    async fetchAllDestinations () {
      const summary = await this.fetchTractContentAllocationSummary()

      const speciesMap = new Map()

      const locationName = item => this.allLocationsByAccountId[item.destinationAccountId]?.name ?? ''

      summary.sort((a, b) => locationName(a).localeCompare(locationName(b))).forEach(item => {
        const location = this.allLocationsByAccountId[item.destinationAccountId]
        if (!location) return

        if (!speciesMap.has(item.speciesId)) {
          speciesMap.set(item.speciesId, {
            species: item.species,
            tractContentAllocations: [],
            harvestUpdatedOn: item.harvestUpdatedOn
          })
        }
        speciesMap.get(item.speciesId).tractContentAllocations.push({
          destinationAccountName: location.name,
          expected: item.sumExpected,
          harvested: item.sumHarvested,
          harvestUpdatedOn: item.harvestUpdatedOn
        })
      })

      this.chartData = Array.from(speciesMap.values()).sort((a, b) =>
        a.species.localeCompare(b.species)
      )
    },

    async updateChartData () {
      if (!this.selectedLocation) return

      this.loading = true

      try {
        if (this.selectedLocation.locationId === -1) {
          await this.fetchAllDestinations()
        } else {
          await this.fetchSingleDestination(this.selectedLocation)
        }
      } catch (error) {
        console.error('Failed to fetch allocation summary:', error)
        this.chartData = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
